import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Phone, MapPin, Send, CheckCircle, Loader2, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import SEO from '../components/SEO';
import { buildWhatsAppLink, trackWhatsAppClick } from '../components/FloatingWhatsAppButton';
import { companyInfo } from '../mockData';
import { buildEmailLink } from '../lib/email';
import { SocialLinks } from '../components/SocialLinks';
import { useLanguage } from '../i18n/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_LABELS = {
  'paper-chemicals': 'Paper Chemicals',
  'construction-chemicals': 'Construction Chemicals',
  'ss-wire-mesh': 'SS Wire Mesh',
  'high-pressure-showers': 'High Pressure Showers & Nozzles',
  'dyes': 'Dyes',
  'general-inquiry': 'General Products'
};

const Contact = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    interestedIn: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Build smart WhatsApp message from form data
  const buildSmartWhatsAppMessage = () => {
    const parts = [];
    
    if (formData.name) {
      const company = formData.company ? ` from ${formData.company}` : '';
      parts.push(`Hi VB Global, I'm ${formData.name}${company}.`);
    } else {
      parts.push("Hi VB Global,");
    }

    const productLabel = PRODUCT_LABELS[formData.interestedIn];
    if (productLabel) {
      parts.push(`I'm interested in ${productLabel}.`);
    }

    if (formData.message) {
      parts.push(formData.message);
    }

    if (!formData.message && !productLabel) {
      parts.push("Please share details about your products.");
    }

    if (formData.phone) {
      parts.push(`\nMy contact: ${formData.phone}`);
    }
    if (formData.email) {
      parts.push(`Email: ${formData.email}`);
    }

    return parts.join(' ');
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        setSubmitted(true);
        // Reset form after 5 seconds
        setTimeout(() => {
          setSubmitted(false);
          setFormData({
            name: '',
            email: '',
            phone: '',
            company: '',
            message: '',
            interestedIn: ''
          });
        }, 5000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Contact Us"
        description="Contact VB Global for paper chemicals, construction chemicals, SS wire mesh and high pressure showers & nozzles. Located at Coimbatore-641022. Phone: +91 78670 96977. Email: enquiry.vbglobal@gmail.com"
        keywords="VB Global contact, paper chemicals supplier Coimbatore, contact construction chemicals supplier, industrial chemicals enquiry, Coimbatore chemical supplier, paper mill chemicals enquiry, get quote paper chemicals"
      />
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#8B2C3A] to-[#6B1C2A] text-white">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 data-testid="contact-hero-title" className="text-4xl md:text-5xl font-bold mb-6">
              {t('contact.heroTitle')}
            </h1>
            <p className="text-xl text-gray-200">
              {t('contact.heroSub')}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Information Cards */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="border-2 hover:border-[#8B2C3A] transition-colors">
                <CardHeader>
                  <div className="w-12 h-12 bg-[#8B2C3A] rounded-lg flex items-center justify-center mb-4">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <CardTitle>{t('contact.phone')}</CardTitle>
                  <CardDescription>
                    <a href={`tel:${companyInfo.phone}`} className="text-gray-900 hover:text-[#8B2C3A] transition-colors font-medium">
                      {companyInfo.phone}
                    </a>
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card className="border-2 hover:border-[#25D366] transition-colors bg-emerald-50">
                <CardHeader>
                  <div className="w-12 h-12 bg-[#25D366] rounded-lg flex items-center justify-center mb-4">
                    <MessageCircle className="w-6 h-6 text-white" />
                  </div>
                  <CardTitle>{t('contact.whatsapp')}</CardTitle>
                  <CardDescription className="space-y-3">
                    <p className="text-gray-700">{t('contact.whatsappDesc')}</p>
                    <a
                      href={buildWhatsAppLink(buildSmartWhatsAppMessage())}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackWhatsAppClick('contact_card', '/contact')}
                      className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold px-4 py-2 rounded-md transition-colors text-sm"
                      data-testid="contact-whatsapp-card-button"
                    >
                      <MessageCircle className="w-4 h-4" />
                      {t('contact.chatWhatsApp')}
                    </a>
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card className="border-2 hover:border-[#8B2C3A] transition-colors">
                <CardHeader>
                  <div className="w-12 h-12 bg-[#8B2C3A] rounded-lg flex items-center justify-center mb-4">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <CardTitle>{t('contact.email')}</CardTitle>
                  <CardDescription className="space-y-3">
                    <a
                      href={buildEmailLink()}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-email-link"
                      className="block text-gray-900 hover:text-[#8B2C3A] transition-colors font-medium break-all"
                    >
                      {companyInfo.email}
                    </a>
                    <p className="text-gray-700">{t('contact.emailDesc')}</p>
                    <a
                      href={buildEmailLink()}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-email-button"
                      className="inline-flex items-center gap-2 bg-[#8B2C3A] hover:bg-[#6B1C2A] text-white px-4 py-2 rounded-lg transition-colors font-medium"
                    >
                      <Mail className="w-4 h-4" />
                      {t('contact.sendEmail')}
                    </a>
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card className="border-2 hover:border-[#8B2C3A] transition-colors">
                <CardHeader>
                  <div className="w-12 h-12 bg-[#8B2C3A] rounded-lg flex items-center justify-center mb-4">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <CardTitle>{t('contact.location')}</CardTitle>
                  <CardDescription className="text-gray-900 font-medium">
                    {companyInfo.address}
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card className="border-2 bg-emerald-50 border-emerald-200">
                <CardHeader>
                  <CardTitle className="text-emerald-800">{t('contact.businessHours')}</CardTitle>
                  <CardDescription className="text-emerald-700">
                    {t('contact.hours1')}<br />
                    {t('contact.hours2')}<br />
                    {t('contact.hours3')}
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card className="border-2 hover:border-[#8B2C3A] transition-colors" data-testid="contact-social-card">
                <CardHeader>
                  <CardTitle>{t('contact.followUs')}</CardTitle>
                  <CardDescription className="space-y-3">
                    <p className="text-gray-700">{t('contact.followUsDesc')}</p>
                    <SocialLinks variant="light" showLabels />
                  </CardDescription>
                </CardHeader>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-2">
                <CardHeader>
                  <CardTitle className="text-2xl">{t('contact.formTitle')}</CardTitle>
                  <CardDescription>
                    {t('contact.formSub')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {submitted ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="w-10 h-10 text-emerald-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('contact.sent')}</h3>
                      <p className="text-gray-600">{t('contact.sentDesc')}</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="name">{t('contact.name')}</Label>
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder={t('contact.phName')}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">{t('contact.emailLabel')}</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="your.email@example.com"
                            required
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="phone">{t('contact.phoneLabel')}</Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="+91 98765 43210"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="company">{t('contact.company')}</Label>
                          <Input
                            id="company"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            placeholder={t('contact.phCompany')}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="interestedIn">{t('contact.interestedIn')}</Label>
                        <select
                          id="interestedIn"
                          name="interestedIn"
                          value={formData.interestedIn}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8B2C3A]"
                        >
                          <option value="">{t('contact.selectCategory')}</option>
                          <option value="paper-chemicals">{t('categories.paper-chemicals.name')}</option>
                          <option value="construction-chemicals">{t('categories.construction-chemicals.name')}</option>
                          <option value="ss-wire-mesh">{t('categories.ss-wire-mesh.name')}</option>
                          <option value="high-pressure-showers">{t('categories.high-pressure-showers.name')}</option>
                          <option value="dyes">{t('categories.dyes.name')}</option>
                          <option value="general-inquiry">{t('contact.generalInquiry')}</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message">{t('contact.message')}</Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder={t('contact.phMessage')}
                          rows={6}
                          required
                        />
                      </div>

                      {error && (
                        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 text-red-700 text-sm">
                          {error}
                        </div>
                      )}

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Button 
                          type="submit" 
                          size="lg" 
                          disabled={submitting}
                          className="w-full bg-[#8B2C3A] hover:bg-[#6B1C2A] text-white disabled:opacity-60"
                          data-testid="contact-submit-button"
                        >
                          {submitting ? (
                            <>
                              <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                              {t('contact.sending')}
                            </>
                          ) : (
                            <>
                              {t('contact.sendMessage')}
                              <Send className="ml-2 w-5 h-5" />
                            </>
                          )}
                        </Button>

                        <a
                          href={buildWhatsAppLink(buildSmartWhatsAppMessage())}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackWhatsAppClick('contact_form', '/contact')}
                          className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold rounded-md h-11 px-8 text-base transition-colors"
                          data-testid="contact-whatsapp-form-button"
                        >
                          <MessageCircle className="w-5 h-5" />
                          {t('contact.sendWhatsApp')}
                        </a>
                      </div>

                      <p className="text-xs text-gray-500 text-center">
                        {t('contact.tip')}
                      </p>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section (Placeholder) */}
      <section className="py-0">
        <div className="w-full h-96 bg-gray-300">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3915.7536!2d76.9558!3d11.0168!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sCoimbatore%2C%20Tamil%20Nadu!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="VB Global Location"
          ></iframe>
        </div>
      </section>
    </div>
  );
};

export default Contact;
